import { AttachMoney, ChevronRight, ContentCopy } from '@mui/icons-material';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import SchemaOutlined from '@mui/icons-material/SchemaOutlined';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { API } from 'aws-amplify';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  ALLIANCE_APPS,
  buildRecruitLink,
} from '../../components/alliance/alliance-apps';
import { AppContext } from '../../contexts/app-context';
import { uicolors } from '../../theme';
import { captureError } from '../../utils/capture-error';
import { getErrorMessage } from '../../utils/error-message';

// Shared card look for every quick link
const cardSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: 1.5,
  width: '100%',
  height: '100%',
  minHeight: 88,
  p: 2,
  textAlign: 'left',
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  color: uicolors.blueNav[950],
  boxShadow: 1,
  transition: 'box-shadow 150ms, border-color 150ms',
  '&:hover, &:focus-visible': {
    boxShadow: 3,
    borderColor: ALLIANCE_APPS.dashboard.accent,
  },
  '&.Mui-disabled': { opacity: 0.6 },
} as const;

/** Card contents: colored avatar icon on the left, title and subtext right. */
function CardBody(props: {
  icon: React.ReactNode;
  color: string;
  label: string;
  description: string;
}) {
  return (
    <>
      <Avatar
        sx={{
          bgcolor: props.color,
          width: 48,
          height: 48,
          flexShrink: 0,
        }}
      >
        {props.icon}
      </Avatar>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ minWidth: 0, width: '100%' }}
      >
        <Box>
          <Typography
            component="span"
            sx={{
              display: 'block',
              fontWeight: 700,
              fontSize: 15,
              lineHeight: 1.3,
            }}
          >
            {props.label}
          </Typography>
          <Typography
            component="span"
            sx={{
              display: 'block',
              mt: 0.25,
              fontSize: 13,
              lineHeight: 1.35,
              color: 'text.secondary',
            }}
          >
            {props.description}
          </Typography>
        </Box>
        {props.label !== 'Copy Recruit Link' && <ChevronRight />}
      </Stack>
    </>
  );
}

/**
 * Card that copies the agent's registration (recruit) link. The GUID comes
 * from Contracting's `/register/get-uuid`, which resolves it from the signed-in
 * user's token, so the card is disabled while impersonating (it would return
 * the signed-in user's own link).
 */
function RecruitLinkCard() {
  // Context
  const {
    state: { impersonatedAgent },
  } = React.useContext(AppContext);

  // State
  const [copied, setCopied] = React.useState(false);
  const [copyFailed, setCopyFailed] = React.useState(false);

  // Query - Registration GUID (fetched on click)
  const pathUuid = '/register/get-uuid';
  const queryUuid = useQuery({
    enabled: false,
    queryKey: ['ContractingAPI', pathUuid],
    queryFn: async () => {
      const response: { data: string } = await API.post(
        'ContractingAPI',
        pathUuid,
        {},
      );
      return response.data;
    },
    staleTime: Infinity,
    onError: (error) => captureError({ data: { error } }),
  });

  // Reset the "Copied" confirmation after a moment
  React.useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => setCopied(false), 2500);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const handleCopy = async () => {
    setCopyFailed(false);
    const result = queryUuid.data
      ? { data: queryUuid.data }
      : await queryUuid.refetch();
    if (!result.data) return;

    try {
      await navigator.clipboard.writeText(
        buildRecruitLink({ uuid: result.data }),
      );
      setCopied(true);
    } catch (error) {
      setCopyFailed(true);
      captureError({ data: { error } });
    }
  };

  return (
    <>
      <ButtonBase
        sx={cardSx}
        disabled={Boolean(impersonatedAgent) || queryUuid.isFetching}
        onClick={handleCopy}
        title={
          impersonatedAgent
            ? 'Stop impersonating to copy your recruit link'
            : undefined
        }
      >
        <CardBody
          color={copied ? '#2e7d32' : '#C99B45'}
          icon={copied ? <CheckCircleOutlined /> : <ContentCopy />}
          label={
            copied
              ? 'Link copied'
              : queryUuid.isFetching
                ? 'Getting link…'
                : 'Copy Recruit Link'
          }
          description={
            copied
              ? 'Paste it to share'
              : 'Copy your registration link to invite new agents'
          }
        />
      </ButtonBase>
      {(queryUuid.isError || copyFailed) && (
        <Alert severity="error" sx={{ gridColumn: '1 / -1' }}>
          {queryUuid.isError
            ? getErrorMessage({
                error: queryUuid.error,
                fallback: 'Please try again.',
              })
            : 'We couldn’t copy the link. Please try again.'}
        </Alert>
      )}
    </>
  );
}

/**
 * Row of quick-link cards under the featured promo: three page links and a
 * button that copies the agent's recruit link.
 */
export function QuickLinks() {
  const links = [
    // {
    //   label: 'Welcome Videos',
    //   to: '/resources/welcome-videos',
    //   description: 'Get started with introductory videos',
    //   color: ALLIANCE_APPS.dashboard.bar,
    //   icon: <VideoLibraryOutlined />,
    // },
    {
      label: 'Commissions',
      to: '/business/commissions',
      description: 'Track your earnings',
      color: '#C99B45',
      icon: <AttachMoney sx={{ fontSize: 32 }} />,
    },
    {
      label: 'Hierarchy Production Drawing',
      to: '/team/hierarchy-production-drawing',
      description: 'View production across your team',
      color: ALLIANCE_APPS.dashboard.bar,
      icon: <SchemaOutlined sx={{ transform: 'rotate(-90deg)' }} />,
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: {
          xs: '1fr',
          md: 'repeat(3, 1fr)',
        },
      }}
    >
      {links.map((link) => (
        <ButtonBase
          key={link.to}
          component={RouterLink}
          to={link.to}
          sx={cardSx}
        >
          <CardBody
            icon={link.icon}
            color={link.color}
            label={link.label}
            description={link.description}
          />
        </ButtonBase>
      ))}
      <RecruitLinkCard />
    </Box>
  );
}
